import React from 'react';
import { NavLink, Stack, Text, Badge, Collapse, Group, rem } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import {
  IconDashboard,
  IconUsers,
  IconCamera,
  IconActivity,
  IconReport,
  IconSettings,
  IconChevronRight,
  IconSchool,
  IconShield,
} from '@tabler/icons-react';
import { usePermissions } from '../../hooks/useAuth';
import type { NavigationItem, NavigationSection } from '../../types/navigation';
import type { UserRole } from '../../types/auth';

export const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    canManageStudents,
    canManageCameras,
    canViewReports,
    canManageUsers,
    canAccessSettings,
    isAdmin,
  } = usePermissions();


  // Navigation configuration based on user permissions
  const navigationSections: NavigationSection[] = [
    {
      title: 'Main',
      items: [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: IconDashboard,
          roles: ['admin', 'super_admin', 'teacher', 'operator'] as UserRole[],
        },
      ],
    },
    {
      title: 'Management',
      items: [
        ...(canManageStudents() ? [{
          label: 'Students',
          path: '/admin/students',
          icon: IconUsers,
          roles: ['admin', 'super_admin', 'teacher'] as UserRole[],
          children: [
            { label: 'All Students', path: '/admin/students', icon: IconUsers, roles: ['admin', 'super_admin', 'teacher'] as UserRole[] },
            { label: 'Enroll Student', path: '/admin/students/new', icon: IconUsers, roles: ['admin', 'super_admin'] as UserRole[] },
          ],
        }] : []),
        ...(canManageCameras() ? [{
          label: 'Cameras',
          path: '/admin/cameras',
          icon: IconCamera,
          roles: ['admin', 'super_admin', 'operator'] as UserRole[],
          children: [
            { label: 'All Cameras', path: '/admin/cameras', icon: IconCamera, roles: ['admin', 'super_admin', 'operator'] as UserRole[] },
            { label: 'Add Camera', path: '/admin/cameras/new', icon: IconCamera, roles: ['admin', 'super_admin'] as UserRole[] },
          ],
        }] : []),
        {
          label: 'Activities',
          path: '/admin/activities',
          icon: IconActivity,
          roles: ['admin', 'super_admin', 'teacher', 'operator'] as UserRole[],
        },
        {
          label: 'Safety Alerts',
          path: '/admin/alerts',
          icon: IconShield,
          roles: ['admin', 'super_admin', 'teacher', 'operator'] as UserRole[],
        },
      ].filter(Boolean),
    },
    {
      title: 'Analytics',
      items: [
        ...(canViewReports() ? [{
          label: 'Reports',
          path: '/admin/reports',
          icon: IconReport,
          roles: ['admin', 'super_admin', 'teacher'] as UserRole[],
        }] : []),
      ].filter(Boolean),
    },
    {
      title: 'Administration',
      items: [
        ...(canManageUsers() ? [{
          label: 'Users',
          path: '/admin/users',
          icon: IconUsers,
          roles: ['admin', 'super_admin'] as UserRole[],
        }] : []),
        ...(isAdmin() ? [{
          label: 'Schools',
          path: '/admin/schools',
          icon: IconSchool,
          roles: ['admin', 'super_admin'] as UserRole[],
        }] : []),
        ...(canAccessSettings() ? [{
          label: 'Settings',
          path: '/admin/settings',
          icon: IconSettings,
          roles: ['admin', 'super_admin'] as UserRole[],
        }] : []),
      ].filter(Boolean),
    },
  ].filter(section => section.items.length > 0);


  const NavigationLink: React.FC<{ item: NavigationItem; depth?: number }> = ({ item, depth = 0 }) => {
    const [opened, { toggle }] = useDisclosure(false);
    const isActive = location.pathname === item.path || 
                     (item.children?.some(child => location.pathname === child.path));
    const hasChildren = item.children && item.children.length > 0;

    const handleClick = () => {
      if (hasChildren) {
        toggle();
      } else {
        navigate(item.path);
      }
    };

    return (
      <>
        <NavLink
          key={item.path}
          label={item.label}
          leftSection={<item.icon style={{ width: rem(16), height: rem(16) }} />}
          rightSection={
            <Group gap={4}>
              {item.badge && (
                <Badge size="xs" variant="filled" color="admin">
                  {item.badge}
                </Badge>
              )}
              {hasChildren && (
                <IconChevronRight
                  style={{
                    width: rem(12),
                    height: rem(12),
                    transform: opened ? 'rotate(90deg)' : undefined,
                    transition: 'transform 0.2s ease',
                  }}
                />
              )}
            </Group>
          }
          active={isActive && !hasChildren}
          onClick={handleClick}
          style={{
            paddingLeft: rem(12 + depth * 20),
            borderRadius: rem(6),
            marginBottom: rem(2),
          }}
          classNames={{
            root: 'admin-nav-link',
          }}
        />

        {hasChildren && (
          <Collapse in={opened}>
            <Stack gap={2} ml="md">
              {item.children!.map((child) => (
                <NavigationLink key={child.path} item={child} depth={depth + 1} />
              ))}
            </Stack>
          </Collapse>
        )}
      </>
    );
  };

  return (
    <Stack gap="lg" style={{ height: '100%' }}>
      {navigationSections.map((section, index) => (
        <div key={index}>
          {section.title && (
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              mb="sm"
              style={{ letterSpacing: '0.05em' }}
            >
              {section.title}
            </Text>
          )}
          <Stack gap={2}>
            {section.items.map((item) => (
              <NavigationLink key={item.path} item={item} />
            ))}
          </Stack>
        </div>
      ))}
    </Stack>
  );
};

export default AdminSidebar;